const GRAPH_API_BASE_URL = "https://graph.microsoft.com/v1.0";

/**
 * Gets the full email conversation thread using Microsoft Graph API
 * @param {string} accessToken - The Office access token
 * @param {string} conversationId - The conversation ID to get the thread for
 * @returns {Promise<Object>} The conversation thread data
 */
export const getEmailConversationThread = async (accessToken, conversationId) => {
    try {
        console.log("Getting all messages from conversation ID:", conversationId);
        console.log("Conversation ID length:", conversationId.length);

        // URL encode the conversation ID for OData filter to handle special characters
        const encodedConversationId = encodeURIComponent(conversationId);
        console.log("Encoded conversation ID:", encodedConversationId);

        // Get all messages from the conversation without sorting or top (simplest query)
        const url = `${GRAPH_API_BASE_URL}/me/messages?$filter=conversationId eq '${encodedConversationId}'&$select=subject,from,toRecipients,body,receivedDateTime,internetMessageId,conversationId,isRead`;
        console.log("Graph API URL:", url);

        const conversationResponse = await fetch(url, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            }
        });

        console.log("Graph API response status:", conversationResponse.status);

        if (!conversationResponse.ok) {
            const errorText = await conversationResponse.text();
            console.error("Graph API error response:", errorText);
            throw new Error(`Failed to get conversation: ${conversationResponse.status} ${conversationResponse.statusText}. Response: ${errorText}`);
        }

        const conversationData = await conversationResponse.json();
        const allMessages = conversationData.value || [];
        console.log("All conversation messages received:", allMessages.length, "messages");

        if (allMessages.length === 0) {
            return {
                conversation: []
            };
        }

        // Find the newest message client-side by receivedDateTime
        const newestMessage = allMessages.reduce((newest, current) => {
            const newestDate = new Date(newest.receivedDateTime);
            const currentDate = new Date(current.receivedDateTime);
            return currentDate > newestDate ? current : newest;
        });

        console.log("Found newest message:", newestMessage.subject, "received:", newestMessage.receivedDateTime);

        return {
            conversation: [newestMessage] // Return only the newest message as an array
        };
    } catch (error) {
        console.error("Failed to get email conversation thread:", error);
        throw error;
    }
};

/**
 * Gets a specific message by its internet message ID
 * @param {string} accessToken - The Office access token
 * @param {string} internetMessageId - The internet message ID
 * @returns {Promise<Object>} The message data
 */
export const getMessageByInternetMessageId = async (accessToken, internetMessageId) => {
    try {
        const escapedId = (internetMessageId || "").replace(/'/g, "''");
        const url = `${GRAPH_API_BASE_URL}/me/messages?$filter=internetMessageId eq '${escapedId}'&$select=subject,from,toRecipients,body,receivedDateTime,internetMessageId,conversationId`;
        console.log("[graphService] getMessageByInternetMessageId", { internetMessageId });

        const response = await fetch(url, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
            }
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to get message: ${response.status} ${response.statusText}. ${errorText}`);
        }

        const data = await response.json();
        const message = data.value?.[0];
        if (!message) {
            throw new Error("Message not found for internetMessageId");
        }
        return message;
    } catch (error) {
        console.error("Failed to get message by internet message ID:", error);
        throw error;
    }
};

/**
 * Formats the conversation thread into a readable email context
 * @param {Array} conversation - Array of messages in the conversation
 * @returns {string} Formatted email context
 */
export const formatConversationContext = (conversation) => {
    if (!conversation || conversation.length === 0) {
        return "";
    }

    // If we only have one message (the latest), it should contain the full conversation
    if (conversation.length === 1) {
        const message = conversation[0];
        const from = message.from?.emailAddress?.name || message.from?.emailAddress?.address || "Unknown";
        const to = message.toRecipients?.map(recipient =>
            recipient.emailAddress?.name || recipient.emailAddress?.address
        ).join(", ") || "Unknown";

        const subject = message.subject || "No Subject";
        const body = message.body?.content || "";
        const receivedDate = new Date(message.receivedDateTime).toLocaleDateString();

        let formattedContext = `--- Latest Message (${receivedDate}) ---\n`;
        formattedContext += `From: ${from}\n`;
        formattedContext += `To: ${to}\n`;
        formattedContext += `Subject: ${subject}\n\n`;

        // Extract text content from HTML body (this should contain the full conversation)
        const textContent = extractTextFromHtml(body);
        formattedContext += `${textContent}\n\n`;

        return formattedContext;
    }

    // Fallback: if we have multiple messages, format them all
    let formattedContext = "";

    conversation.forEach((message, index) => {
        const from = message.from?.emailAddress?.name || message.from?.emailAddress?.address || "Unknown";
        const to = message.toRecipients?.map(recipient =>
            recipient.emailAddress?.name || recipient.emailAddress?.address
        ).join(", ") || "Unknown";

        const subject = message.subject || "No Subject";
        const body = message.body?.content || "";
        const receivedDate = new Date(message.receivedDateTime).toLocaleDateString();

        formattedContext += `\n--- Message ${index + 1} (${receivedDate}) ---\n`;
        formattedContext += `From: ${from}\n`;
        formattedContext += `To: ${to}\n`;
        formattedContext += `Subject: ${subject}\n\n`;

        // Extract text content from HTML body
        const textContent = extractTextFromHtml(body);
        formattedContext += `${textContent}\n\n`;
    });

    return formattedContext;
};

/**
 * Extracts text content from HTML
 * @param {string} html - HTML content
 * @returns {string} Plain text content
 */
const extractTextFromHtml = (html) => {
    if (!html) return "";

    // Remove HTML tags and decode HTML entities
    const textContent = html
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '') // Remove style tags
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '') // Remove script tags
        .replace(/<[^>]*>/g, '') // Remove all HTML tags
        .replace(/&nbsp;/g, ' ') // Replace non-breaking spaces
        .replace(/&amp;/g, '&') // Replace HTML entities
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/\s+/g, ' ') // Replace multiple spaces with single space
        .trim();

    return textContent;
};

/**
 * Tests if Graph API is accessible with the given access token
 * @param {string} accessToken - The Office access token
 * @returns {Promise<boolean>} True if Graph API is accessible, false otherwise
 */
export const testGraphAPIAccess = async (accessToken) => {
    try {
        const response = await fetch(
            `${GRAPH_API_BASE_URL}/me`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                }
            }
        );

        return response.ok;
    } catch (error) {
        console.error("Graph API test failed:", error);
        return false;
    }
};
